import { DeleteObjectCommand, S3Client } from '@aws-sdk/client-s3'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import { Progress, Upload } from '@aws-sdk/lib-storage'
import { XhrHttpHandler } from '@aws-sdk/xhr-http-handler'

const AWS_REGION = 'eu-west-1'
const IDENTITY_POOL_ID = 'eu-west-1:f53d9a4e-3b29-483f-a166-9dff87ca2941'

export interface UploadFileInput {
  readonly body: Blob
  readonly bucket: string
  readonly contentType?: string
  readonly key: string
}

export type UploadProgress = Progress

export interface UploadTask {
  abort(): Promise<void>
}

export interface UploadOperation {
  readonly completion: Promise<string>
  readonly task: UploadTask
}

interface S3Location {
  readonly bucket: string
  readonly key: string
}

type ExtendedWindow = typeof window & {
  Checksub?: { readonly e2eMode?: boolean }
  EditorSources?: { readonly e2eMode?: boolean }
  Rollbar?: { error: (error: Error, context?: unknown) => void }
}

type CognitoCredentialsProvider = ReturnType<typeof fromCognitoIdentityPool>

let s3Client: S3Client | undefined
let cognitoCredentialsProvider: CognitoCredentialsProvider | undefined
const activeUploadTasks = new Set<UploadTask>()
const uploadCancellationPromises = new WeakMap<UploadTask, Promise<boolean>>()

const getCognitoCredentialsProvider = (): CognitoCredentialsProvider => {
  cognitoCredentialsProvider ||= fromCognitoIdentityPool({
    clientConfig: { region: AWS_REGION },
    identityPoolId: IDENTITY_POOL_ID,
  })

  return cognitoCredentialsProvider
}

const createS3Client = (): S3Client => (
  new S3Client({
    region: AWS_REGION,
    requestHandler: new XhrHttpHandler({}),
    credentials: getCognitoCredentialsProvider(),
  })
)

const getS3Client = (): S3Client => {
  s3Client ||= createS3Client()

  return s3Client
}

// Keep the preparation phase visible long enough for browser assertions before
// transferring the bytes to the same-origin E2E object-store fake.
const E2E_UPLOAD_PROGRESS_DELAY = 1_000

const e2eModeEnabled = (): boolean => {
  const extendedWindow = window as ExtendedWindow
  return extendedWindow.Checksub?.e2eMode === true || extendedWindow.EditorSources?.e2eMode === true
}

const normalizeS3Location = (bucket: string, key: string): S3Location => {
  const separatorIndex = bucket.indexOf('/')

  if (separatorIndex === -1) {
    return { bucket, key }
  }

  const bucketName = bucket.slice(0, separatorIndex)
  const keyPrefix = bucket.slice(separatorIndex + 1).replace(/\/+$/, '')
  const objectKey = key.replace(/^\/+/, '')

  return {
    bucket: bucketName,
    key: [keyPrefix, objectKey].filter(Boolean).join('/'),
  }
}

const e2eObjectUrl = (location: S3Location): string => {
  const bucket = encodeURIComponent(location.bucket)
  const key = location.key.split('/').map(encodeURIComponent).join('/')

  return `${window.location.origin}/e2e/storage/${bucket}/${key}`
}

const deleteE2EObject = async (location: S3Location): Promise<void> => {
  const response = await window.fetch(e2eObjectUrl(location), { method: 'DELETE' })

  if (!response.ok) {
    throw new Error(`E2E object deletion failed with status ${response.status}`)
  }
}

const startE2EUpload = (
  input: UploadFileInput,
  onProgress?: (progress: UploadProgress) => void,
): UploadOperation => {
  const location = normalizeS3Location(input.bucket, input.key)
  const total = input.body.size
  let settled = false
  let cancellationRequested = false
  let uploadRequest: Promise<Response> | undefined
  let resolveCompletion: (url: string) => void = () => undefined
  let rejectCompletion: (reason?: unknown) => void = () => undefined
  const abortController = new AbortController()
  const objectUrl = e2eObjectUrl(location)

  const completion = new Promise<string>((resolve, reject) => {
    resolveCompletion = resolve
    rejectCompletion = reject
  })

  const progressTimer = window.setTimeout(() => {
    if (settled || cancellationRequested) { return }

    onProgress?.({
      loaded: Math.max(1, Math.floor(total / 2)),
      total,
    })

    uploadRequest = window.fetch(objectUrl, {
      body: input.body,
      headers: input.contentType ? { 'Content-Type': input.contentType } : undefined,
      method: 'PUT',
      signal: abortController.signal,
    })

    void uploadRequest.then(response => {
      if (!response.ok) {
        throw new Error(`E2E object upload failed with status ${response.status}`)
      }

      if (settled || cancellationRequested) { return }

      settled = true
      resolveCompletion(objectUrl)
    }).catch((error: unknown) => {
      if (settled || cancellationRequested) { return }

      settled = true
      rejectCompletion(error)
    })
  }, E2E_UPLOAD_PROGRESS_DELAY)

  const task: UploadTask = {
    abort: async () => {
      if (settled) { return }

      cancellationRequested = true
      window.clearTimeout(progressTimer)
      abortController.abort()
      await uploadRequest?.catch(() => undefined)
      await deleteE2EObject(location)

      settled = true
      const error = new Error('Upload cancelled')
      error.name = 'AbortError'
      rejectCompletion(error)
    },
  }

  return { completion, task }
}

export const normalizeStorageError = (error: unknown): Error => (
  error instanceof Error ? error : new Error(String(error))
)

const performUploadAbort = async (task: UploadTask): Promise<boolean> => {
  try {
    await task.abort()
    return true
  } catch (error: unknown) {
    const storageError = normalizeStorageError(error)
    const extendedWindow = window as ExtendedWindow

    extendedWindow.Rollbar?.error(storageError, { operation: 'abort-upload' })
    return false
  }
}

const cancelUploadTask = (task: UploadTask): Promise<boolean> => {
  const existingCancellation = uploadCancellationPromises.get(task)
  if (existingCancellation) { return existingCancellation }

  const cancellation = performUploadAbort(task).then(aborted => {
    if (!aborted) {
      uploadCancellationPromises.delete(task)
    }

    return aborted
  })
  uploadCancellationPromises.set(task, cancellation)

  return cancellation
}

export const abortUpload = (task: UploadTask): Promise<boolean> => cancelUploadTask(task)

export const hasActiveUploads = (): boolean => activeUploadTasks.size > 0

export const cancelActiveUploads = (): Promise<boolean[]> => (
  Promise.all(Array.from(activeUploadTasks, cancelUploadTask))
)

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (activeUploadTasks.size === 0) { return }

  // Browsers do not expose the user's Leave/Stay choice or allow this handler
  // to await. Begin cancellation before showing the native confirmation so the
  // SDK gets the maximum possible time to stop the transfer.
  void cancelActiveUploads()
  event.preventDefault()
  event.returnValue = 'An upload is still in progress.'
}

const trackActiveUpload = (operation: UploadOperation): UploadOperation => {
  const isFirstActiveUpload = activeUploadTasks.size === 0
  activeUploadTasks.add(operation.task)

  if (isFirstActiveUpload) {
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', cancelActiveUploads)
  }

  const stopTracking = () => {
    activeUploadTasks.delete(operation.task)

    if (activeUploadTasks.size === 0) {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', cancelActiveUploads)
    }
  }

  void operation.completion.then(stopTracking, stopTracking)

  return operation
}

export const startUpload = (
  input: UploadFileInput,
  onProgress?: (progress: UploadProgress) => void,
): UploadOperation => {
  if (e2eModeEnabled()) {
    return trackActiveUpload(startE2EUpload(input, onProgress))
  }

  const location = normalizeS3Location(input.bucket, input.key)
  const upload = new Upload({
    // lib-storage listens for progress on the client's XHR handler. That
    // handler is an EventEmitter, and its multipart filtering only considers
    // the part number. Sharing it makes simultaneous uploads consume each
    // other's byte events, so every upload needs its own transport instance.
    client: createS3Client(),
    params: {
      ACL: 'public-read',
      Body: input.body,
      Bucket: location.bucket,
      ContentType: input.contentType,
      Key: location.key,
    },
  })

  if (onProgress) {
    upload.on('httpUploadProgress', onProgress)
  }

  let cancellationRequested = false
  let cancellationOutcome: Promise<boolean> | undefined
  const task: UploadTask = {
    abort: async () => {
      // Invalidate the completion synchronously. The SDK abort can fail while the
      // upload still completes, in which case completion applies the caller's
      // selected ownership cleanup.
      cancellationRequested = true
      let abortRequest: Promise<void>

      try {
        abortRequest = upload.abort()
      } catch (error: unknown) {
        cancellationOutcome = Promise.resolve(false)
        throw error
      }

      cancellationOutcome = abortRequest.then(() => true, () => false)
      await abortRequest
    },
  }
  const completion = upload.done().then(async result => {
    if (!result.Location) {
      await deleteObject(location.bucket, location.key)
      throw new Error('S3 upload completed without a file location')
    }

    if (cancellationRequested) {
      await cancellationOutcome

      await deleteObject(location.bucket, location.key)
      const error = new Error('Upload cancelled')
      error.name = 'AbortError'
      throw error
    }

    return decodeURI(result.Location)
  })

  return trackActiveUpload({ completion, task })
}

export const deleteObject = async (bucket: string, key: string): Promise<void> => {
  const location = normalizeS3Location(bucket, key)

  if (e2eModeEnabled()) {
    await deleteE2EObject(location)
    return
  }

  await getS3Client().send(new DeleteObjectCommand({ Bucket: location.bucket, Key: location.key }))
}
